import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { submitBrotherForm, getEventById } from '../../utils/api';
import { getBrotherData } from '../../utils/auth';
import { QuestionInput } from './EventUI';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.subtle};
  padding: 2rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
  text-align: left;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #4a5568;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.accent};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: #2c5282;
  }

  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
`;

const Error = styled.div`
  color: #e53e3e;
  margin-top: 0.5rem;
  font-size: 0.875rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  ${props => props.$type === 'success'
    ? `
      background-color: #c6f6d5;
      color: #276749;
      border: 1px solid #9ae6b4;
    ` 
    : `
      background-color: white;
      color: black;
    `}

  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
`;


const BrotherEventForm = ({ eventId }) => {
  const [event, setEvent] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', content: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const brother = getBrotherData();
  const fraternityId = brother?.frat;

  const fetchEventDetails = useCallback(async () => {
    if (!fraternityId) return;
    try {
      const response = await getEventById(eventId, fraternityId);
      if (response.data?.success) {
        setEvent(response.data.data);
      } else {
        throw new Error('Failed to fetch event details');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setMessage({
        type: 'error',
        content: 'Failed to load event details. Please try again.'
      });
    }
  }, [eventId, fraternityId]);

  useEffect(() => {
    if (!fraternityId) {
      navigate('/login');
      return;
    }
    fetchEventDetails();
  }, [fraternityId, fetchEventDetails, navigate]);

  const validateForm = () => {
    const newErrors = {};
    event.brotherForm.questions.forEach((question, index) => {
      if (question.required && !formResponses[index]) {
        newErrors[index] = 'This field is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (index, value) => {
    setFormResponses(prev => ({
      ...prev,
      [index]: value
    }));
    
    if (errors[index]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setMessage({ type: '', content: '' });

    try {
      await submitBrotherForm(eventId, formResponses);
      setMessage({
        type: 'success',
        content: 'Form submitted successfully!'
      });
      setFormResponses({});
    } catch (error) {
      setMessage({
        type: 'error',
        content: error.response?.data?.error || 'Failed to submit form'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question, index) => {
    return <QuestionInput question={question} index={index} value={formResponses[index]} onChange={handleInputChange} namePrefix="brother-question" />;
    /*
    switch (question.questionType) {
      case 'text':
        return (
          <Input
            type="text"
            value={formResponses[index] || ''}
            onChange={(e) => handleInputChange(index, e.target.value)}
            placeholder="Your answer"
          />
        );

      case 'textarea':
        return (
          <TextArea
            value={formResponses[index] || ''}
            onChange={(e) => handleInputChange(index, e.target.value)}
            placeholder="Your answer"
          />
        );

      case 'rating':
        return (
          <RatingContainer>
            {[...Array(10)].map((_, i) => (
              <RatingButton
                key={i + 1}
                type="button"
                selected={formResponses[index] === (i + 1).toString()}
                onClick={() => handleInputChange(index, (i + 1).toString())}
              >
                {i + 1}
              </RatingButton>
            ))}
          </RatingContainer>
        );

      case 'multipleChoice':
        return (
          <ChoiceContainer>
            {question.options.map((option, i) => (
              <ChoiceLabel key={i}>
                <input
                  type="radio"
                  name={`question-${index}`}
                  checked={formResponses[index] === option}
                  onChange={() => handleInputChange(index, option)}
                />
                {option}
              </ChoiceLabel>
            ))}
          </ChoiceContainer>
        );

      case 'checkbox':
        return (
          <ChoiceContainer>
            {question.options.map((option, i) => (
              <ChoiceLabel key={i}>
                <input
                  type="checkbox"
                  checked={(formResponses[index] || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = formResponses[index] || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(value => value !== option);
                    handleInputChange(index, newValues);
                  }}
                />
                {option}
              </ChoiceLabel>
            ))}
          </ChoiceContainer>
        );

      default:
        return <Input type="text" />;
    }
    */
  };

  if (!event) {
    return (
      <FormContainer>
        <Card>
          <Message $type="error">Loading event form…</Message>
        </Card>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Card>
        <Title>{event.name}</Title>
        
        {message.content && (
          <Message $type={message.type}>
            {message.content}
          </Message>
        )}

        <form onSubmit={handleSubmit}>
          {event.brotherForm.questions.map((question, index) => (
            <FormGroup key={index}>
              <Label>
                {question.question}
                {question.required && ' *'}
              </Label>
              {renderQuestion(question, index)}
              {errors[index] && <Error>{errors[index]}</Error>}
            </FormGroup>
          ))}

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Form'}
          </Button>
        </form>
      </Card>
    </FormContainer>
  );
};

export default BrotherEventForm;